import { z } from 'zod';
import { localeSchema } from './common';

export const checkoutInput = z.object({
  priceId: z.string(),
  /**
   * Kod se šalje kao tekst, nikad kao iznos — server ga sam preračuna.
   * Inače bi korisnik mogao vidjeti jednu cijenu, a poslati drugu.
   */
  discountCode: z.string().min(2).max(40).optional(),
  locale: localeSchema.default('hr'),
  successPath: z.string().startsWith('/').default('/checkout/uspjeh'),
  cancelPath: z.string().startsWith('/').default('/cjenik'),
});

export const checkoutOutput = z.object({
  url: z.string().url(),
  sessionId: z.string(),
  /** Popust koji je stvarno primijenjen na Stripe sesiju; null kad koda nema. */
  discount: z
    .object({ code: z.string(), amountOffCents: z.number().int().nonnegative().nullable() })
    .nullable()
    .default(null),
});

export const entitlementOutput = z.object({
  productId: z.string(),
  productSlug: z.string(),
  productName: z.string(),
  /** `trial` je izvor kao i svaki drugi — probni pristup je zapis u `entitlements`. */
  source: z.enum(['order', 'subscription', 'manual', 'trial']),
  /** `trial` = probni pristup bez plaćanja; samo `full` znači plaćen paket. */
  kind: z.enum(['full', 'trial']),
  activeFrom: z.string(),
  activeUntil: z.string().nullable(),
  /** Izračunato na serveru — klijent nikad sam ne odlučuje ima li pristup. */
  active: z.boolean(),
});

export const entitlementsOutput = z.object({
  items: z.array(entitlementOutput),
  hasActiveAccess: z.boolean(),
});

export const portalSessionOutput = z.object({ url: z.string().url() });

export const subscriptionStatusSchema = z.enum([
  'active',
  'past_due',
  'canceled',
  'expired',
  'none',
]);

/**
 * `GET /v1/billing/subscription` — ekran „Pretplata" u portalu.
 *
 * Sekcija 4 arhitekture traži „pretplata, računi, povijest plaćanja" pod
 * `/v1/billing`, ali rute nije bilo: portal je zvao nepostojeći URL i radio
 * samo nad mockom. Status se izvodi iz `subscriptions` i `entitlements`,
 * nikad iz Stripe odgovora uživo — pravilo 4.
 */
export const subscriptionOutput = z.object({
  plan: z.object({ name: z.string(), priceLabel: z.string() }).nullable(),
  status: subscriptionStatusSchema,
  currentPeriodEnd: z.string().nullable(),
  cancelAt: z.string().nullable(),
  /** Dokad vrijedi pristup — i za jednokratne kupnje (First Move = 90 dana). */
  accessUntil: z.string().nullable(),
  invoices: z.array(
    z.object({
      id: z.string(),
      issuedAt: z.string(),
      amountCents: z.number().int(),
      currency: z.string(),
      status: z.string(),
      hostedUrl: z.string().nullable(),
    }),
  ),
});

export type CheckoutInput = z.infer<typeof checkoutInput>;
export type CheckoutOutput = z.infer<typeof checkoutOutput>;
export type EntitlementsOutput = z.infer<typeof entitlementsOutput>;
export type SubscriptionOutput = z.infer<typeof subscriptionOutput>;
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
